// Copyright 2026, University of Maryland, All Rights Reserved
import { useCallback, useMemo, useState, type FC } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Info, Plug } from 'lucide-react'

import {
  Alert,
  AlertDescription,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui'
import { ConfirmDialog } from '@/components/forms'
import { useServices } from '@/app-shell/services'
import { workspaceDocumentsQueryKey } from '../api/workspaceDocumentsQuery'
import { useWorkspaceConfig } from '@/app-shell/hooks'
import { emitWorkspaceChange } from '@/app-shell/events'
import type { NodeUserViewProps } from '@/core/types'
import { DocumentUploadZone, useDocumentUpload } from '@/subsystems/documents'
import { useAgents, type Agent } from '@/subsystems/agents'

import type { DocumentListItemWithPipelines } from '../models'
import type { AgentRunHistory } from '../hooks/useWorkspaceAgentRuns'
import { useWorkspaceAgentRuns } from '../hooks/useWorkspaceAgentRuns'
import { useRendererPreview } from '../hooks/useRendererPreview'
import { useWorkspaceRuns } from '../hooks/useWorkspaceRuns'
import { rendererRef } from '@/subsystems/agents'
import { agentAcceptsDocument } from '../lib/agentRunRules'
import { DashboardStatus } from './DashboardStatus'
import { ConnectorSyncStrip } from '@/subsystems/vault/connectors/components/ConnectorSyncStrip'
import { ManageRecordsDialog } from '@/subsystems/vault/connectors/components/ManageRecordsDialog'
import { VersionPanel } from './VersionPanel'
import { WorkspaceDocumentsTable } from './WorkspaceDocumentsTable'
import { WorkspaceRunDialog } from './WorkspaceRunDialog'
import { WorkspaceRunStatus } from './WorkspaceRunStatus'

interface UploadsDashboardProps extends NodeUserViewProps {
  wsId?: string
}

export const UploadsDashboard: FC<UploadsDashboardProps> = ({
  workspaceId,
  orgId,
  wsId: wsIdProp,
  config,
}) => {
  const services = useServices()
  const apiClient = services.getClient()
  const resolvedOrgId = orgId?.trim() ?? ''
  const wsId = wsIdProp ?? workspaceId ?? ''
  const { groupDocumentsByExternalId, connectorIntakeEnabled } = useWorkspaceConfig(resolvedOrgId, wsId)
  const sourceGrouped = groupDocumentsByExternalId ?? false

  const [message, setMessage] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [pendingDelete, setPendingDelete] = useState<DocumentListItemWithPipelines | null>(null)
  const [deleteInFlight, setDeleteInFlight] = useState(false)
  const [versionPanelDocId, setVersionPanelDocId] = useState<string | null>(null)
  const [pullRecordsOpen, setPullRecordsOpen] = useState(false)

  const basePath = useMemo(
    () => `/org/${resolvedOrgId}/ws/${wsId}/contract-review`,
    [resolvedOrgId, wsId],
  )
  const documentsQueryKey = useMemo(
    () => workspaceDocumentsQueryKey(resolvedOrgId, wsId),
    [resolvedOrgId, wsId],
  )
  const queryClient = useQueryClient()

  const { data: documents = [], isPending: loading } = useQuery<DocumentListItemWithPipelines[]>({
    queryKey: documentsQueryKey,
    enabled: Boolean(resolvedOrgId && wsId),
    queryFn: async () => {
      const { data } = await apiClient.get<{ documents: DocumentListItemWithPipelines[] }>(
        `/org/${resolvedOrgId}/ws/${wsId}/documents?include_pipeline_status=true`,
      )
      return data.documents ?? []
    },
  })
  const { data: workspaceAgents = [] } = useAgents(apiClient, resolvedOrgId, wsId)
  const configuredAgent = config?.agent as Agent | undefined
  const agents = useMemo(
    () => configuredAgent ? [configuredAgent] : workspaceAgents,
    [configuredAgent, workspaceAgents],
  )
  const visibleDocuments = useMemo(
    () => configuredAgent
      ? documents.filter((document) => agentAcceptsDocument(configuredAgent, document))
      : documents,
    [configuredAgent, documents],
  )
  const { runs: runHistory, refresh: refreshRunHistory } = useWorkspaceAgentRuns(
    apiClient,
    resolvedOrgId,
    wsId,
    agents,
  )

  const refreshDocuments = useCallback(() => {
    return queryClient.invalidateQueries({ queryKey: documentsQueryKey })
  }, [queryClient, documentsQueryKey])

  const renderers = useMemo(
    () => Object.fromEntries(agents.flatMap((agent) =>
      Object.entries(agent.renderers).map(([rendererId, definition]) =>
        [rendererRef(agent.agentId, rendererId), { ...definition, agentId: agent.agentId }] as const,
      ),
    )),
    [agents],
  )
  const pipelineIdsByRenderer = useMemo(
    () => Object.fromEntries(agents.flatMap((agent): [string, string][] => {
      const pipelineId = agent.pipelineId
      return pipelineId
        ? Object.keys(agent.renderers).map((rendererId) => [rendererRef(agent.agentId, rendererId), pipelineId])
        : []
    })),
    [agents],
  )
  const preview = useRendererPreview({
    apiClient,
    orgId: resolvedOrgId,
    workspaceId: wsId,
    renderers,
    documents,
    pipelineIdsByRenderer,
    onError: setError,
  })

  const { uploadMultiple, uploading } = useDocumentUpload({
    apiClient,
    orgId: resolvedOrgId,
    wsId,
    onSuccess: refreshDocuments,
  })
  const runs = useWorkspaceRuns({
    apiClient,
    orgId: resolvedOrgId,
    workspaceId: wsId,
    onStatusChange: refreshDocuments,
    agents,
    documents,
    onAllComplete: () => {
      setError(null)
      setMessage(null)
      refreshDocuments()
      void refreshRunHistory()
    },
    onError: (runError) => {
      setMessage(null)
      setError(runError)
    },
  })

  const handleDownloadLatest = useCallback(async (document: DocumentListItemWithPipelines) => {
    try {
      const { data } = await apiClient.get<{ url: string }>(
        `/org/${resolvedOrgId}/ws/${wsId}/documents/${document.documentId}/view-url?version=${document.latestVersion}`,
      )
      window.open(data.url, '_blank')
    } catch (downloadError: any) {
      setError(downloadError?.response?.data?.detail ?? downloadError?.message ?? 'Failed to download')
    }
  }, [apiClient, resolvedOrgId, wsId])

  const handleConfirmDelete = useCallback(async () => {
    if (!pendingDelete) return
    setDeleteInFlight(true)
    setError(null)
    setMessage(null)
    try {
      const groupParam = pendingDelete.groupId ? `?group_id=${encodeURIComponent(pendingDelete.groupId)}` : ''
      await apiClient.delete(
        `/org/${resolvedOrgId}/ws/${wsId}/contract-review/documents/${encodeURIComponent(pendingDelete.documentId)}${groupParam}`,
      )
      setMessage(`Deleted ${pendingDelete.latestFilename ?? pendingDelete.name}`)
      refreshDocuments()
      emitWorkspaceChange({ orgId: resolvedOrgId, wsId, action: 'document-deleted' })
    } catch (deleteError: any) {
      setError(deleteError?.response?.data?.detail ?? deleteError?.message ?? 'Failed to delete document')
    } finally {
      setDeleteInFlight(false)
      setPendingDelete(null)
    }
  }, [apiClient, pendingDelete, refreshDocuments, resolvedOrgId, wsId])

  if (!resolvedOrgId || !wsId) {
    return <p className="text-sm text-destructive">Contract Review requires organization and workspace IDs.</p>
  }

  return (
    <div className="space-y-4">
      {connectorIntakeEnabled && (
        <ConnectorSyncStrip api={apiClient} orgId={resolvedOrgId} workspaceId={wsId} />
      )}
      <Card className={sourceGrouped ? 'overflow-hidden border-border shadow-sm' : 'border-border'}>
        <CardHeader className={sourceGrouped ? 'gap-1.5 px-5 pb-0 pt-[18px]' : 'gap-1.5'}>
          <CardTitle className={sourceGrouped ? 'text-[17px] font-semibold' : 'text-xl font-semibold'}>{configuredAgent?.label ?? 'Contract Review'}</CardTitle>
          <CardDescription className="flex items-center gap-2 text-sm text-slate-500">
            <span className="rounded-md bg-slate-100 px-2 py-0.5 font-mono text-xs text-slate-600">
              {wsId}
            </span>
            <span>{documents.length} document{documents.length === 1 ? '' : 's'}</span>
          </CardDescription>
        </CardHeader>
        <CardContent className={sourceGrouped ? 'space-y-3 px-5 pb-5 pt-3.5' : 'space-y-4 pb-6'}>
          {!sourceGrouped && (
            <Alert>
              <Info className="h-4 w-4" />
              <AlertDescription>
                To analyze a document, open the Run menu in the Actions column and choose an
                agent. Runs appear as a view action when complete.
              </AlertDescription>
            </Alert>
          )}
          <div className={sourceGrouped ? 'flex flex-col gap-3 xl:flex-row xl:items-stretch' : 'flex flex-col gap-3 lg:flex-row'}>
            <div className="min-w-0 flex-1">
              <DocumentUploadZone onUpload={uploadMultiple} uploading={uploading} />
            </div>
            {connectorIntakeEnabled && (
              <Button variant="outline" className={sourceGrouped ? 'h-auto min-h-10 shrink-0 rounded-lg px-4 text-xs font-semibold' : 'h-auto min-h-11 shrink-0 px-5'} onClick={() => setPullRecordsOpen(true)}>
                <Plug className="mr-2 h-4 w-4" /> Manage source records
              </Button>
            )}
          </div>
          <DashboardStatus message={message} error={error} />
          <WorkspaceDocumentsTable
            documents={visibleDocuments}
            loading={loading}
            agents={agents}
            runHistory={runHistory}
            optimisticRunning={runs.optimisticRunning}
            mode={sourceGrouped ? 'source-groups' : 'flat'}
            onRun={runs.open}
            onView={(document, run: AgentRunHistory, version) =>
              preview.openVersionForRenderer(document, run.rendererId, version)}
            onDownload={(document) => void handleDownloadLatest(document)}
            onManageVersions={setVersionPanelDocId}
            onDelete={setPendingDelete}
          />
        </CardContent>
      </Card>

      <WorkspaceRunStatus entries={runs.entries} onClear={runs.clearEntries} />

      <WorkspaceRunDialog
        open={runs.pending !== null}
        agents={runs.pendingAgents}
        documents={runs.pendingDocuments}
        running={runs.isRunning}
        onOpenChange={(open) => { if (!open) runs.close() }}
        onConfirm={(versions) => void runs.confirm(versions)}
      />

      <VersionPanel
        documentId={versionPanelDocId}
        basePath={basePath}
        apiClient={apiClient}
        onClose={() => setVersionPanelDocId(null)}
        onVersionUploaded={refreshDocuments}
        allowChanges={documents.find((document) => document.documentId === versionPanelDocId)?.sourceProvider === null}
        onDeleteDocument={(documentId) => {
          const document = documents.find((item) => item.documentId === documentId)
          setVersionPanelDocId(null)
          if (document) setPendingDelete(document)
        }}
      />
      <ConfirmDialog
        open={pendingDelete !== null}
        onOpenChange={(open) => { if (!open) setPendingDelete(null) }}
        title="Delete document"
        description={pendingDelete ? `Delete "${pendingDelete.latestFilename ?? pendingDelete.name}"? This action cannot be undone.` : ''}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        variant="destructive"
        loading={deleteInFlight}
        onConfirm={handleConfirmDelete}
      />
      {preview.dialogs}
      {connectorIntakeEnabled && (
        <ManageRecordsDialog
          api={apiClient}
          orgId={resolvedOrgId}
          workspaceId={wsId}
          open={pullRecordsOpen}
          onOpenChange={setPullRecordsOpen}
          onQueued={refreshDocuments}
        />
      )}
    </div>
  )
}
